/**
 * Custom hooks for analytics data fetching and management.
 */

import { useState, useEffect, useCallback } from 'react';
import { analyticsService, AdminDashboardData, HostDashboardData, GuestDashboardData, AnalyticsFilters, ExportRequest } from '../services/analyticsService';

export interface UseAnalyticsReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for admin dashboard analytics
 * @param filters Optional filters to apply to the analytics data
 */
export const useAdminAnalytics = (filters?: AnalyticsFilters): UseAnalyticsReturn<AdminDashboardData> => {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await analyticsService.getAdminDashboard(filters);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch admin analytics');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for host dashboard analytics
 * @param filters Optional filters to apply to the analytics data
 */
export const useHostAnalytics = (filters?: AnalyticsFilters): UseAnalyticsReturn<HostDashboardData> => {
  const [data, setData] = useState<HostDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await analyticsService.getHostDashboard(filters);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch host analytics');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for guest dashboard analytics
 */
export const useGuestAnalytics = (): UseAnalyticsReturn<GuestDashboardData> => {
  const [data, setData] = useState<GuestDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await analyticsService.getGuestDashboard();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch guest analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for filtered analytics
 */
export const useFilteredAnalytics = (filters: AnalyticsFilters) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await analyticsService.getFilteredAnalytics(filters);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch filtered analytics');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (filters.start_date || filters.end_date || filters.category_id || filters.host_id) {
      fetchData();
    }
  }, [filters.start_date, filters.end_date, filters.category_id, filters.host_id, fetchData]);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for analytics export functionality
 */
export const useAnalyticsExport = () => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportEvents = async (exportRequest: ExportRequest) => {
    try {
      setExporting(true);
      setError(null);
      const { blob, contentType } = await analyticsService.exportEventsAnalytics(exportRequest);
      // Ensure filename has correct extension
      const extension = exportRequest.format === 'excel' ? 'xlsx' : 'csv';
      const filename = `events_analytics.${extension}`;
      analyticsService.downloadFile(blob, filename, contentType);
    } catch (err: any) {
      setError(err.message || 'Failed to export events analytics');
    } finally {
      setExporting(false);
    }
  };

  const exportGuests = async (exportRequest: ExportRequest) => {
    try {
      setExporting(true);
      setError(null);
      const { blob, contentType } = await analyticsService.exportGuestsAnalytics(exportRequest);
      // Ensure filename has correct extension
      const extension = exportRequest.format === 'excel' ? 'xlsx' : 'csv';
      const filename = `guests_analytics.${extension}`;
      analyticsService.downloadFile(blob, filename, contentType);
    } catch (err: any) {
      setError(err.message || 'Failed to export guests analytics');
    } finally {
      setExporting(false);
    }
  };

  return {
    exporting,
    error,
    exportEvents,
    exportGuests
  };
};
